import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Leaf, Mountain, Flame } from "lucide-react";
import { BRAND_ASSETS, PHOTOGRAPHY } from "../lib/constants";
import { fetchProducts } from "../lib/api";
import Overline from "../components/Overline";
import Reveal from "../components/Reveal";
import PatternStrip from "../components/PatternStrip";

const Home = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden noise">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-20 pb-24 md:pb-32">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                <Overline>From Sidama · Ethiopia</Overline>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif-display text-[68px] sm:text-[96px] md:text-[140px] leading-[0.9] text-elle-green mt-6 tracking-tight"
              >
                Ell'<span className="italic">e</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif-display italic text-2xl md:text-3xl text-elle-soft mt-6 max-w-xl"
              >
                Experience What We Have Seen.
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.55 }}
                className="mt-8 max-w-lg text-[15px] leading-relaxed text-elle-soft"
              >
                A single-origin expression of the highlands of Sidama — grown
                slowly in volcanic soil, picked by hand, shared in ceremony. In
                every cup, a memory of the land.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.75 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/shop"
                  data-testid="hero-shop-btn"
                  className="group inline-flex items-center gap-3 bg-elle-green text-elle-cream px-8 py-4 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-green-deep)] transition-colors duration-500"
                >
                  Shop Now
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-story-btn"
                  className="link-underline text-[11px] tracking-overline uppercase text-elle-green"
                >
                  Read our story
                </Link>
              </motion.div>
            </div>

            <div className="md:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] rounded-sm overflow-hidden shadow-[0_30px_80px_-30px_rgba(44,56,18,0.35)]"
              >
                <img
                  src={BRAND_ASSETS.bagFront}
                  alt="Ell'e Coffee Premium Sidamo"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, rotate: -4 }}
                animate={{ opacity: 1, rotate: -4 }}
                transition={{ duration: 1, delay: 0.6 }}
                className="absolute -bottom-8 -left-6 md:-left-10 w-40 md:w-48 bg-elle-red text-elle-cream p-5 shadow-xl hidden sm:block"
              >
                <Overline color="cream">Since 2024</Overline>
                <div className="font-serif-display text-2xl mt-2 leading-tight">
                  100% Arabica · Single Origin
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        <PatternStrip height={10} />
      </section>

      {/* MARQUEE */}
      <section className="bg-elle-green text-elle-cream py-6 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 pr-10">
              {["What I Have Seen", "From Sidama to Your Cup", "100% Arabica", "Single Origin", "Heritage · Experience", "1,800 – 2,200 m"].map((t, i) => (
                <React.Fragment key={`${k}-${i}`}>
                  <span className="font-serif-display italic text-3xl md:text-4xl">{t}</span>
                  <span className="text-elle-cream/40 text-2xl">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* BRAND STORY PREVIEW */}
      <section className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-16 items-center">
          <Reveal className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={PHOTOGRAPHY.ceremony}
                alt="Ethiopian coffee ceremony"
                className="w-full h-full object-cover hover-lift"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <Overline>The Story</Overline>
            <h2 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-6 leading-[1.05]">
              "What I have seen" —<br/>
              <span className="italic">carried in every bean.</span>
            </h2>
            <p className="mt-8 text-[16px] md:text-[17px] leading-relaxed text-elle-soft max-w-xl">
              In the Sidama language, <em>Ell'e</em> means <em>what I have seen</em>.
              It is a word for memory, heritage, and the quiet knowledge passed
              between generations. Our coffee is a tribute to that — to Maya,
              to family, to the misted mornings of the Sidama highlands.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="inline-flex items-center gap-3 mt-10 text-[11px] tracking-overline uppercase text-elle-green link-underline"
            >
              Discover our heritage
              <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-20 md:py-28 bg-elle-muted" data-testid="home-featured-products">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <Overline>Our Coffees</Overline>
              <h2 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-4 leading-[1.05]">
                Two origins.<br/>
                <span className="italic">One heritage.</span>
              </h2>
            </div>
            <Link to="/shop" className="link-underline text-[11px] tracking-overline uppercase text-elle-green" data-testid="home-view-all">
              View the shop →
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            {products.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link
                  to={`/product/${p.slug}`}
                  data-testid={`featured-product-${p.slug}`}
                  className="block group"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-elle-sand">
                    <img
                      src={p.image_front}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                    />
                    <div className="absolute top-5 left-5 bg-elle-cream/90 backdrop-blur-sm px-3 py-1.5">
                      <span className="text-[10px] tracking-overline uppercase text-elle-green">
                        {p.slug === "sidamo" ? "Est. Origin" : "Floral Lift"}
                      </span>
                    </div>
                  </div>
                  <div className="mt-6 flex items-end justify-between gap-4">
                    <div>
                      <Overline className="!text-elle-green/60">{p.subtitle}</Overline>
                      <h3 className="font-serif-display text-3xl md:text-4xl text-elle-green mt-2">
                        {p.name.replace("Ell'e Coffee — ", "")}
                      </h3>
                      <p className="text-[13px] text-elle-soft mt-2 max-w-sm">
                        {p.description}
                      </p>
                    </div>
                    <div className="font-serif-display text-2xl text-elle-green whitespace-nowrap">
                      ${p.price_500g.toFixed(0)}
                      <span className="text-xs text-elle-soft ml-1">/500g</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ELL'E */}
      <section className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Why Ell'e</Overline>
            <h2 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-4 max-w-3xl leading-[1.05]">
              A coffee rooted in place, hand, and ceremony.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-10 md:gap-14 mt-20">
            {[
              { icon: Mountain, title: "Origin", text: "Grown in the Sidama highlands between 1,800 – 2,200m, where altitude and volcanic soil shape a slow, deliberate bean." },
              { icon: Leaf, title: "Quality", text: "100% single-origin Arabica. Hand-picked, washed, carefully graded — no blends, no shortcuts." },
              { icon: Flame, title: "Heritage", text: "Ethiopia is the birthplace of coffee. Every bag is a tribute to the land, to Maya, and to the ceremonies that still mark time." },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 0.12}>
                <div className="group">
                  <item.icon size={28} className="text-elle-red" />
                  <div className="mt-6 border-t border-elle pt-6">
                    <h3 className="font-serif-display text-3xl text-elle-green">
                      {item.title}
                    </h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-elle-soft">
                      {item.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CULTURAL CTA STRIP */}
      <section className="relative overflow-hidden">
        <div className="bg-elle-green text-elle-cream py-24 md:py-32 relative">
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
            <Overline color="cream">An invitation</Overline>
            <h2 className="font-serif-display text-5xl md:text-7xl mt-8 leading-[1.05]">
              Taste what we have <span className="italic">seen.</span>
            </h2>
            <p className="mt-8 max-w-xl mx-auto text-elle-cream/80">
              Order your first bag and share in the ceremony. Every cup tells
              the story of Sidama.
            </p>
            <Link
              to="/shop"
              data-testid="cta-strip-shop"
              className="inline-flex items-center gap-3 mt-12 bg-elle-cream text-elle-green px-8 py-4 text-[11px] tracking-overline uppercase hover:bg-white transition-colors duration-500"
            >
              Explore the coffees
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <PatternStrip height={10} />
      </section>
    </div>
  );
};

export default Home;
